import type { ArrangementStatus } from '../schema/arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2] DUTY ANALYTICS — THE CLOSED VOCABULARIES. Runtime values only, zod-free, so the
 *  officer app's budget could read them and the web page reads them without the schema barrel.
 *
 *  ⚠️ NO SCORE, NO RANK, NO PERCENTAGE ANYWHERE IN THIS FEATURE (§2d). Every figure is a raw
 *  COUNT; the one comparison that needs a ratio — the best mapping card — is decided on the server
 *  by integer cross-multiplication, so no ratio exists in a payload, a type or a renderer.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The presets: trailing IST days, ending today. `z.literal(TUPLE)`, never `z.enum` over numbers (register #23).
 *
 * [amended P7C.1 §5] A preset ends today; a CUSTOM window (`dateFrom`/`dateTo`) ends today or earlier, never
 * later, and covers at most `DUTY_ANALYTICS_MAX_WINDOW_DAYS`.
 */
export const DUTY_ANALYTICS_RANGES = [1, 7, 30] as const;
export type DutyAnalyticsRange = (typeof DUTY_ANALYTICS_RANGES)[number];
export const DUTY_ANALYTICS_DEFAULT_RANGE: DutyAnalyticsRange = 7;

/**
 * ⚠️ [P7C.1 §5] THE CUSTOM WINDOW'S SPAN CAP IS THE LARGEST PRESET, DERIVED — never a second number. The user
 * capped it at thirty days because thirty is what the section budgets were measured at
 * (`dutyAnalyticsScale.test.ts`), so the cap and the presets can never disagree, and the page keeps ONE day-span
 * bound. `ATTENDANCE_MAX_RANGE_DAYS` belongs to two dashboard tiles and does not reach this page.
 */
export const DUTY_ANALYTICS_MAX_WINDOW_DAYS: number = Math.max(...DUTY_ANALYTICS_RANGES);

/**
 * [P7C.1 §5] The custom window's three refusals, worded once for the API (as its 422's message) and the web (as
 * the line under the date pickers). `dutyAnalyticsWindowRefusal` in `utils/dutyAnalyticsWindow.util.ts` decides
 * which applies; these are the words alone, so the constants barrel stays free of runtime imports.
 */
export const DUTY_ANALYTICS_REVERSED_REFUSAL =
  'The start date is after the end date. Choose a start on or before the end.';
export const DUTY_ANALYTICS_FUTURE_END_REFUSAL =
  'The end date is after today. Duty analytics counts what has already happened, so choose an end on or before today.';
export const dutyAnalyticsSpanRefusal = (days: number): string =>
  `A custom range can cover at most ${String(DUTY_ANALYTICS_MAX_WINDOW_DAYS)} days, and the dates chosen cover ${String(days)}. Choose a shorter range.`;

/**
 * ⚠️ TWO STATIC LAYOUTS (§2c), CHOSEN BY THE CALLER'S POSITION — never by the tile registry. A
 * GLOBAL scope and a nodal acting at the commissionerate root read COMMISSIONERATE; a nodal acting
 * at a depth-1 division reads DIVISION; anything else gets no page (`DUTY_ANALYTICS_LEVEL_REFUSAL`).
 */
export const DUTY_ANALYTICS_LAYOUTS = ['COMMISSIONERATE', 'DIVISION'] as const;
export type DutyAnalyticsLayout = (typeof DUTY_ANALYTICS_LAYOUTS)[number];

/**
 * Every figure on the page, each of which drills through to its rows (§2j).
 *
 * ⚠️ `MARKED_ON_BEHALF` / `MARKED_SELF` ARE ATTENDANCE MARKINGS ON FORCE SUBJECTS, NEVER A COMMAND
 * COUNT (U1). A commander's marking for another officer is one row; an officer's own is the other;
 * a commander's own attendance and the command seats are counted nowhere — FR-5.2.6b, [C6a].
 */
export const DUTY_ANALYTICS_METRICS = [
  'ASKED',
  'NAMED',
  'DELEGATED',
  'DECLARED_SHORT',
  'LEFT_OPEN',
  'REACHED',
  'ABSENT',
  'MARKED_ON_BEHALF',
  'MARKED_SELF',
] as const;
export type DutyAnalyticsMetric = (typeof DUTY_ANALYTICS_METRICS)[number];

/** What one drill row IS. `DUTY` rows are counts per duty and name NO officer (§2h: no per-officer record). */
export const DUTY_ANALYTICS_DRILL_GRAINS = ['SHARE', 'DELEGATION', 'DUTY'] as const;
export type DutyAnalyticsDrillGrain = (typeof DUTY_ANALYTICS_DRILL_GRAINS)[number];

export const DUTY_ANALYTICS_METRIC_GRAIN = {
  ASKED: 'SHARE',
  NAMED: 'SHARE',
  DECLARED_SHORT: 'SHARE',
  LEFT_OPEN: 'SHARE',
  DELEGATED: 'DELEGATION',
  REACHED: 'DUTY',
  ABSENT: 'DUTY',
  MARKED_ON_BEHALF: 'DUTY',
  MARKED_SELF: 'DUTY',
} as const satisfies Record<DutyAnalyticsMetric, DutyAnalyticsDrillGrain>;

/**
 * The four headline cards (§2e), in render order.
 *  · `MOST_ASKED` — the row asked for the most duties.
 *  · `BEST_MAPPING` — among rows asked for at least the median (U4), the one that named the largest
 *    share of what it was asked; decided by cross-multiplication and shown as raw counts.
 *  · `ASKED_AND_NAMED` — the view's own totals, asked beside named.
 *  · `OPEN_AND_DECLARED` — shares left open beside short-falls declared, side by side, because the
 *    two read alike and are opposite acts: one division has not answered, the other has (§2f).
 */
export const DUTY_ANALYTICS_CARD_KINDS = [
  'MOST_ASKED',
  'BEST_MAPPING',
  'ASKED_AND_NAMED',
  'OPEN_AND_DECLARED',
] as const;
export type DutyAnalyticsCardKind = (typeof DUTY_ANALYTICS_CARD_KINDS)[number];

/**
 * ⚠️ WHICH ORDERS ARE COUNTED — EXHAUSTIVE, SO A SEVENTH STATUS IS A COMPILE ERROR.
 *
 * Issued orders, current or FILED: filing an order that ran must never erase its history. A
 * CANCELLED order never ran (the archive writes it only when no duty window opened and no duty
 * event exists), and a DRAFT was never asked of anybody.
 */
export const DUTY_ANALYTICS_COUNTED_STATUS = {
  DRAFT: false,
  PUBLISHED: true,
  ALLOCATION_IN_PROGRESS: true,
  ACTIVE: true,
  CLOSED: true,
  CANCELLED: false,
} as const satisfies Record<ArrangementStatus, boolean>;

/** The drill page's default page size; the schema caps it at `PAGINATION.MAX_LIMIT`. */
export const DUTY_ANALYTICS_DRILL_LIMIT = 50;

/** The 403 for a caller acting anywhere but the commissionerate or a division. The web's notice reads it too. */
export const DUTY_ANALYTICS_LEVEL_REFUSAL =
  'Duty analytics is available at the commissionerate and at a division. Your access is at a unit, so there is no page for it.';
